import type { AgentPart } from "../chat/agent-event.ts";

/**
 * Lo que es costo, para esconderlo a quien no es superadmin
 * (`canSeeCosts`). Solo interfaz: el API lo sirve igual.
 */

/** Las claves que llevan costo o consumo de IA, a cualquier profundidad. */
const COST_KEYS = new Set(["usage", "costUsd"]);

/**
 * El mismo valor sin costos: quita `usage` y `costUsd` en objetos y
 * arreglos a cualquier profundidad --los pasos guardan el consumo anidado
 * (`details.model.usage`, `details.llmCalls[].usage`) y la ficha lo junta
 * todo--, y las columnas de tabla cuya clave es de costo.
 */
export const withoutCosts = <T>(value: T): T => strip(value) as T;

const isCostColumn = (item: unknown): boolean =>
  typeof item === "object" &&
  item !== null &&
  "key" in item &&
  typeof item.key === "string" &&
  COST_KEYS.has(item.key);

const strip = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value
      .filter((item: unknown) => !isCostColumn(item))
      .map((item: unknown) => strip(item));
  }
  if (typeof value !== "object" || value === null) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !COST_KEYS.has(key))
      .map(([key, inner]) => [key, strip(inner)]),
  );
};

/**
 * Las herramientas del catálogo que hablan de costos: el consumo y los
 * precios de los modelos.
 */
export const COST_TOOLS: ReadonlySet<string> = new Set([
  "get_usage_summary",
  "list_model_prices",
  "sync_model_prices",
  "set_model_price",
]);

/**
 * Las partes de un mensaje sin costos: fuera las llamadas a las
 * herramientas de costo, su tarjeta de confirmación y el componente que
 * pintan justo detrás --la parte `ui_component` no dice de qué herramienta
 * viene, así que se toma la que la sigue--; y lo demás, sin `usage` ni
 * `costUsd`. El texto del agente no se toca: lo que diga, lo dice.
 */
export const withoutCostParts = (parts: readonly AgentPart[]): AgentPart[] => {
  const kept: AgentPart[] = [];
  let afterCostTool = false;
  for (const part of parts) {
    switch (part.type) {
      case "tool":
        afterCostTool = COST_TOOLS.has(part.toolName);
        if (!afterCostTool) {
          kept.push({
            ...part,
            ...(part.input === undefined
              ? {}
              : { input: withoutCosts(part.input) }),
            ...(part.output === undefined
              ? {}
              : { output: withoutCosts(part.output) }),
          });
        }
        break;
      case "confirmation_request":
        if (!COST_TOOLS.has(part.toolName)) kept.push(part);
        break;
      case "ui_component":
        if (!afterCostTool) {
          kept.push({
            ...part,
            props: withoutCosts(part.props),
          });
        }
        break;
      default:
        afterCostTool = false;
        kept.push(part);
    }
  }
  return kept;
};
